/**
 * Releases: release lanes per project, build history as BuildCards ("feedback on this build"),
 * and the loop's speed — feedback → build and landing → build latency.
 */
import { useQuery } from '@tanstack/react-query'
import { Button, Chip } from '@viko/transcript-ui'
import { useMemo, useState } from 'react'
import { BuildView, FeedbackSheet } from '../components/HubCards.js'
import {
  Empty,
  errorText,
  PageHead,
  SectionTitle,
  SkeletonRows,
  useToast,
} from '../components/ui.js'
import { useBackend } from '../lib/backend.js'
import { useBuilds, useLandings, useProjects } from '../lib/queries.js'
import type { BuildCard } from '../lib/types.js'
import { feedbackToBuildMs, formatDur, landToBuildMs } from '../lib/work.js'

/** Start the next build of a lane now (signed `land.deploy`; internal lanes need a tap, production a passkey). */
function StartBuild({
  projectId,
  lane,
  machine,
}: {
  projectId: string
  lane: string
  machine: string
}) {
  const b = useBackend()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  return (
    <Button
      size="sm"
      disabled={busy}
      onClick={async () => {
        setBusy(true)
        try {
          await b.dispatch(
            'land.deploy',
            machine,
            { projectId, lane },
            { summary: `Build ${lane}` },
          )
          toast('Build started')
        } catch (e) {
          toast(errorText(e), 'error')
        } finally {
          setBusy(false)
        }
      }}
    >
      {busy ? 'Starting…' : 'Build now'}
    </Button>
  )
}

/** ia-shell: `projectId` fixes the project (a project tab's Builds view: no heading, no filter). */
export function ReleasesPage({ projectId: fixed }: { projectId?: string } = {}) {
  const projects = useProjects()
  const [picked, setProjectId] = useState<string | undefined>()
  const projectId = fixed ?? picked
  const builds = useBuilds(projectId)
  const landings = useLandings(projectId)
  const b = useBackend()
  const tasks = useQuery({
    queryKey: ['tasks', projectId ?? '*', 'all'],
    queryFn: () => b.api.tasks(projectId ? { projectId } : {}),
    retry: false,
  })
  const [feedback, setFeedback] = useState<BuildCard | null>(null)
  const list = builds.data ?? []
  const lanes = useMemo(() => {
    const m = new Map<string, BuildCard[]>()
    for (const c of list)
      m.set(`${c.projectId}/${c.laneId}`, [...(m.get(`${c.projectId}/${c.laneId}`) ?? []), c])
    return [...m.entries()]
  }, [list])
  const configured = (projects.data ?? [])
    .filter((p) => !projectId || p.id === projectId)
    .flatMap((p) => (p.config?.release?.lanes ?? []).map((l) => ({ p, l })))
  const name = (id: string) => projects.data?.find((p) => p.id === id)?.name ?? id
  const fb = feedbackToBuildMs(list, tasks.data ?? [])
  const lb = landToBuildMs(list, landings.data ?? [])
  return (
    <div className={fixed ? 'min-w-0' : 'app-list flex-1'}>
      <div className={fixed ? 'max-w-[860px] pb-6' : 'mx-auto max-w-[860px] pb-10'}>
        {fixed ? null : <PageHead title="Releases" />}
        <div
          className="choice px-4 pb-2"
          role="radiogroup"
          aria-label="Project filter"
          hidden={!!fixed}
        >
          <button
            type="button"
            role="radio"
            aria-checked={!projectId}
            onClick={() => setProjectId(undefined)}
          >
            All
          </button>
          {(projects.data ?? []).map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={projectId === p.id}
              onClick={() => setProjectId(p.id)}
            >
              {p.name}
            </button>
          ))}
        </div>
        <dl className="stats px-4">
          <div>
            <dt>Feedback → build</dt>
            <dd>{formatDur(fb)}</dd>
          </div>
          <div>
            <dt>Landing → build</dt>
            <dd>{formatDur(lb)}</dd>
          </div>
          <div>
            <dt>Builds</dt>
            <dd>{list.length}</dd>
          </div>
          <div>
            <dt>Failed</dt>
            <dd>{list.filter((c) => c.status === 'failed').length}</dd>
          </div>
        </dl>
        {configured.length ? (
          <section aria-labelledby="rel-lanes">
            <SectionTitle>
              <span id="rel-lanes">Lanes</span>
            </SectionTitle>
            <ul className="m-0 list-none p-0">
              {configured.map(({ p, l }) => {
                const last = list.find((c) => c.projectId === p.id && c.laneId === l.id)
                return (
                  <li key={`${p.id}/${l.id}`} className="row !cursor-default items-center">
                    <span className="grid min-w-0 flex-1 gap-0.5">
                      <span className="text-[15px] font-medium">
                        {l.name ?? l.id} <span className="text-text-3">· {p.name}</span>
                      </span>
                      <span className="row__meta">
                        {l.target ?? 'custom'}
                        {last ? ` · last: ${last.title} (${last.status})` : ' · no builds yet'}
                      </span>
                    </span>
                    {p.config?.machines.allowed.length || l.machine ? (
                      <StartBuild
                        projectId={p.id}
                        lane={l.id}
                        machine={l.machine ?? p.config?.machines.allowed[0] ?? ''}
                      />
                    ) : null}
                    {l.production ? (
                      <Chip tone="wait">production · passkey</Chip>
                    ) : (
                      <Chip>internal</Chip>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>
        ) : null}
        {builds.isPending ? <SkeletonRows n={3} /> : null}
        {builds.isError ? (
          <Empty title="Could not load builds">{errorText(builds.error)}</Empty>
        ) : null}
        {builds.isSuccess && !list.length ? (
          <Empty title="No builds yet">
            When a landing triggers a release lane (TestFlight, web preview, Steam beta…), each
            build shows up here with what it includes.
          </Empty>
        ) : null}
        {lanes.map(([key, cards]) => (
          <section key={key} aria-label={key}>
            <SectionTitle>
              {cards[0]?.laneId}{' '}
              <span className="text-text-3">{name(cards[0]?.projectId ?? '')}</span>
            </SectionTitle>
            <div className="grid gap-3 px-4">
              {cards.slice(0, 10).map((c) => (
                <BuildView
                  key={c.buildId}
                  card={c}
                  project={name(c.projectId)}
                  onFeedback={() => setFeedback(c)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
      {feedback ? <FeedbackSheet build={feedback} onClose={() => setFeedback(null)} /> : null}
    </div>
  )
}
